import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userApi, type WatchlistItem, type WatchlistListingSummary } from '../../services/userApi';
import { ListingCard } from '../../components/search/ListingCard';
import type { SearchHit, SearchHitAttributes } from '../../services/searchApi';

function toSearchHit(item: WatchlistItem): SearchHit {
  const listing: WatchlistListingSummary | null | undefined = item.listing;
  const attributes: SearchHitAttributes = (listing?.attributes as SearchHitAttributes) ?? {};
  return {
    listingId: item.productListingId,
    productId: null,
    title: listing?.title ?? 'Untitled listing',
    retailer: listing?.retailer ?? { id: null, name: null },
    price: listing?.price ?? null,
    pricePerRound: listing?.pricePerRound ?? null,
    roundCount: listing?.roundCount ?? null,
    stockStatus: listing?.stockStatus ?? 'unknown',
    lastUpdated: listing?.lastUpdated ?? null,
    attributes,
    isSponsored: false,
    dataFreshness: listing?.dataFreshness ?? 'unavailable',
    visitUrl: listing?.sourceUrl ?? '#',
    sourceUrl: listing?.sourceUrl ?? null,
  };
}

/**
 * Watchlist page. Renders each watched listing using the same
 * ListingCard the search results page uses, plus per-item "Remove" and
 * "Visit retailer" buttons. Freshness chip is inherited from
 * ListingCard so stale data is visually distinct (spec freshness rule).
 */
export function WatchlistPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['user', 'watchlist'],
    queryFn: () => userApi.listWatchlist(),
  });

  const [confirmRemove, setConfirmRemove] = useState<WatchlistItem | null>(null);

  const removeMutation = useMutation({
    mutationFn: (id: string) => userApi.removeWatchlist(id),
    onSuccess: () => {
      setConfirmRemove(null);
      return queryClient.invalidateQueries({ queryKey: ['user', 'watchlist'] });
    },
  });

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Watchlist
      </Typography>

      {query.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading watchlist" />
        </Box>
      ) : query.isError ? (
        <Alert severity="error" role="alert">
          Failed to load your watchlist: {(query.error as Error).message}
        </Alert>
      ) : (query.data?.watchlist.length ?? 0) === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1">Your watchlist is empty.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add a listing from a product page to watch its price and stock.
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
          }}
        >
          {query.data!.watchlist.map((item) => {
            const hit = toSearchHit(item);
            return (
              <Stack key={item.id} spacing={1}>
                <ListingCard hit={hit} variant="full" />
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    component="a"
                    href={item.listing?.sourceUrl ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit retailer for ${item.listing?.title ?? 'this listing'} (opens in a new tab)`}
                    disabled={!item.listing?.sourceUrl}
                  >
                    Visit retailer
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setConfirmRemove(item)}
                    aria-label={`Remove ${item.listing?.title ?? 'this listing'} from watchlist`}
                  >
                    Remove
                  </Button>
                </Stack>
              </Stack>
            );
          })}
        </Box>
      )}

      <Dialog
        open={Boolean(confirmRemove)}
        onClose={() => setConfirmRemove(null)}
        aria-labelledby="confirm-remove-watch-title"
      >
        <DialogTitle id="confirm-remove-watch-title">Remove from watchlist?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{confirmRemove?.listing?.title ?? 'This listing'}” will be removed from your watchlist.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={removeMutation.isPending}
            onClick={() => confirmRemove && removeMutation.mutate(confirmRemove.id)}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WatchlistPage;
